import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import LandingNavBar from "../../components/common/landingNavBar";
import GameCard from "../../components/common/gameCard";
import UserInfo from "../../components/layouts/userInfo";
import GameDialog from "../../components/common/gameDialog";
import { RiSearchLine } from "react-icons/ri";
import { landingDatas } from "../../components/layouts/landingComponents/landingDatas";
import PlayButton from "../../components/common/playButton";
import { getGamesApi, getVendorsApi } from "../../services/authApi";
import { t, getLang } from "../../lang";
export default function Slot({
  showLogin,
  isAuthenticated,
  userData,
  userBalance,
  onSignOut,
}) {
  const observer = useRef(null);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [vendorsData, setVendorsData] = useState([]);
  const [gamesData, setGamesData] = useState([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [showGameDialog, setShowGameDialog] = useState(false);
  const [isInitFromUrl, setIsInitFromUrl] = useState(true);
  const [isUrlSynced, setIsUrlSynced] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showVendors, setShowVendors] = useState(true);
  const [lang, setLangState] = useState(getLang());

  useEffect(() => {
    const handler = () => setLangState(getLang());
    window.addEventListener("langChanged", handler);
    return () => window.removeEventListener("langChanged", handler);
  }, []);

  const init = {
    search: "",
    page: 1,
    perPage: 48,
    vendor: null,
  };
  const [filter, setFilter] = useState(init);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const page = 1;
    const search = searchParams.get("search") || "";
    const vendor = searchParams.get("vendor");
    setFilter((prev) => ({
      ...prev,
      page,
      search,
      vendor,
    }));
    setSearchInput(search);
    setIsUrlSynced(true);
  }, []);
  useEffect(() => {
    const params = {};
    if (filter.page > 1) params.page = filter.page;
    if (filter.search) params.search = filter.search;
    if (filter.vendor) params.vendor = filter.vendor;
    setSearchParams(params, { replace: true });
  }, [filter.page, filter.search, filter.vendor]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilter((prev) => ({
        ...prev,
        search: searchInput,
        page: 1,
      }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const handleGameCardClick = (game) => {
    if (!isAuthenticated) {
      showLogin();
    } else {
      const isDesktop = window.innerWidth >= 1024; // Example breakpoint for desktop
      if (isDesktop) {
        const url = `/game?gameCode=${game.gameCode}&vendorCode=${game.vendorCode}`;
        const windowName = `game_${game.gameCode}_${Date.now()}`;

        window.open(
          url,
          windowName,
          "width=1280,height=900,toolbar=no,menubar=no,location=no,status=no"
        );
      } else {
        setSelectedGame(game);
        setShowGameDialog(true);
      }
    }
  };

  const handleLoadMore = () => {
    if (isLoadingMore || !isLoadMore) return;

      setIsLoadingMore(true);

      setFilter((prev) => ({
          ...prev,
          page: prev.page + 1,
      }));
  };
  const fetchGames = async (page, reset = false) => {
  if (!isAuthenticated) {
      setGamesData([])
      return;
    }
    try {
      if (reset) {
        setIsLoading(true);
      }

      const userId = JSON.parse(localStorage.getItem("userData"))?.id;

      const res = await getGamesApi({
        userId,
        page,
        perPage: filter.perPage,
        search: filter.search,
        type: null,
        vendor: filter.vendor,
        pageType: "slot"
      });

      const games = res?.data?.data || [];

      setGamesData((prev) => (reset ? games : [...prev, ...games]));

      setIsLoadMore(res.data.has_more);
    } finally {
      if (reset) {
        setIsLoading(false);
      }
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isUrlSynced || !isInitFromUrl) return;
    console.log("filter page:", filter.page);

    (async () => {
      setGamesData([]);
      for (let p = 1; p <= filter.page; p++) {
        await fetchGames(p, p === 1);
      }

      setIsInitFromUrl(false);
    })();
  }, [isUrlSynced]);

  useEffect(() => {
    if (isInitFromUrl) return;

    fetchGames(filter.page, filter.page === 1);
  }, [filter.page, filter.search, filter.vendor, isAuthenticated]);

  // Fetch vendors and games on component mount
  useEffect(() => {
      if (!isAuthenticated) {
        setIsInitialLoading(false);
        setShowVendors(false);
        return;
      }

      const fetchData = async () => {
      try {
        setIsInitialLoading(true);
        const userData = JSON.parse(localStorage.getItem("userData"));
        const userId = userData?.id || null;
        const vendorsResponse = await getVendorsApi({
          userId: userId ? userId : null,
          type: "SLOT",
        });

        // Handle response - check if data is directly an array or wrapped in an object
        const vendors = vendorsResponse?.data?.data || [];

        setVendorsData(Array.isArray(vendors) ? vendors : []);
        setShowVendors(true);
      } catch (error) {
        console.error("Error fetching data:", error);
        setVendorsData([]);
      } finally {
        setIsInitialLoading(false);
      }
    };

    fetchData();
  }, [isAuthenticated]);

  const handleVendorClick = (vendorCode) => {
    setGamesData([]);
    setFilter((prev) => ({
      ...prev,
      vendor: prev.vendor === vendorCode ? null : vendorCode,
      page: 1,
    }));
  };

  const lastElementRef = useCallback(
    (node) => {
        if (isLoadingMore) return;

        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && isLoadMore) {
                    handleLoadMore();
                }
            },
            {
                rootMargin: "200px",
            }
        );

        if (node) observer.current.observe(node);
    },
    [isLoadingMore, isLoadMore]
  );

  const location = useLocation();
  const navigateOther = (link) => {
    const path = `/${link}`;
    if (location.pathname === path) return;
    navigate(path);
  };
  return (
    <>
        {/* <div className="Landing">
          <div
            className="landing-body"
            style={{paddingBottom: '5%', minHeight: '5%' }}
          >
               {isAuthenticated && (
                   <>
                       <div className="landing-search-div">
                           <input
                               className="landing-search"
                               placeholder={t("common.search_games")}
                               value={searchInput}
                               onChange={(e) => setSearchInput(e.target.value)}
                           />
                           <RiSearchLine />
                       </div>
                       <div className='landing-filter' onClick={() => setShowVendors(prev => !prev)}>
                          <span> { t("menu.all_providers") } </span>
                          <img src='/img/icons/Vector.svg' alt='menu' />
                       </div>
                   </>
               )}
          </div>
        </div> */}
      {/* )}
      {!isAuthenticated && (
        <div className="landing-img">
          <img src="/img/newgameback.png" alt="landingImage" />
        </div>
      )} */}
      <div className="landing-img-mobile">
        <img src="/img/newgameback.png" alt="landingImage" />
        {/* Mobile Search */}
          <div className="mobile-search-wrapper">
              <div className="mobile-search">
                  <RiSearchLine className="mobile-search-icon" />
                  <input
                      placeholder={t("common.search_games")}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                  />
              </div>
          </div>
      </div>
      {/* {!isAuthenticated && (
              <div className='mobile-login-button' onClick={showLogin}>
                  Login
              </div>
          )} */}
      {isAuthenticated && (
        <div className="mobile-user-info-container">
          <UserInfo userData={userData} userBalance={userBalance} />
        </div>
      )}

      <LandingNavBar />

      <div className="page-body">
        <div className="newgame-title">
         {/* <span className="page-title">{t('menu.slot')}</span> */}
         <span className="page-title-mobile">{t('menu.slot')}</span>
         {/* <span>{t('menu.supplier')}</span>  */}
        </div>
        <div className="abc">
        {showVendors && (
          <div className="newgame-items">
            {Array.isArray(vendorsData) &&
              vendorsData.map((vendor, index) => (
                <React.Fragment key={vendor.vendorCode}>
                  {vendor.logo ? ( 
                    <div
                      className={`newgame-item-logo ${
                        filter.vendor === vendor.vendorCode ? "active" : ""
                      }`}
                      key={index}
                      onClick={() => handleVendorClick(vendor.vendorCode)}
                    >
                      <div className="vendor-logo-wrapper">
                        <img
                          src={`${process.env.REACT_APP_API_URL}/${vendor.logo}`}
                          alt={vendor.vendorCode}
                          className="vendor-logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `${process.env.REACT_APP_API_URL}/images/logo.png`;
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`newgame-item ${
                        filter.vendor === vendor.vendorCode ? "active" : ""
                      }`}
                      key={index}
                      onClick={() => handleVendorClick(vendor.vendorCode)}
                      style={{ cursor: "pointer" }}
                    >
                      <span>{vendor.vendorCode}</span>
                      <span>{vendor.vendorType}</span>
                    </div>
                  )}
                </React.Fragment>
            ))}
          </div>
        )} 

        {/*{vendorsData?.length > 0 && (*/}
        {/*  <div className="newgame-items-mobile">*/}
        {/*    <div className="newgame-items-mobile-view">*/}
        {/*      {Array.isArray(vendorsData) &&*/}
        {/*        vendorsData.map((vendor, index) => (*/}
        {/*          <div*/}
        {/*            className={`newgame-item-mobile ${*/}
        {/*              filter.vendor === vendor.vendorCode ? "active" : ""*/}
        {/*            }`}*/}
        {/*            key={index}*/}
        {/*            onClick={() => handleVendorClick(vendor.vendorCode)}*/}
        {/*            style={{ cursor: "pointer" }}*/}
        {/*          >*/}
        {/*            {vendor.logo ? (*/}
        {/*              <div*/}
        {/*                className={`newgame-item-logo ${*/}
        {/*                  filter.vendor === vendor.vendorCode ? "active" : ""*/}
        {/*                }`}*/}
        {/*                key={index}*/}
        {/*              >*/}
        {/*                <div className="vendor-logo-wrapper">*/}
        {/*                  <img*/}
        {/*                    src={`${process.env.REACT_APP_API_URL}/${vendor.logo}`}*/}
        {/*                    alt={vendor.vendorCode}*/}
        {/*                    className="vendor-logo"*/}
        {/*                    onError={(e) => {*/}
        {/*                      e.target.onerror = null;*/}
        {/*                      e.target.src = `${process.env.REACT_APP_API_URL}/images/logo.png`;*/}
        {/*                    }}*/}
        {/*                  />*/}
        {/*                </div>*/}
        {/*              </div>*/}
        {/*            ) : (*/}
        {/*              <>*/}
        {/*                <span>{vendor.vendorCode}</span>*/}
        {/*                <span>{vendor.vendorType}</span>*/}
        {/*              </>*/}
        {/*            )}*/}
        {/*          </div>*/}
        {/*        ))}*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*)}*/}

        {/* <div className="newgames-cards"> */}
        {/* <div className="newgame-card">
                      <img src="" alt="card-image"/>
                      <span>starlight princess</span>
                  </div> */}
        {/* <img src="/img/image 3.png" alt="gamecards"/>
              </div> */}
        
        {isInitialLoading || isLoading ? (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <span className="loading-text">{t("common.loading")}</span>
  </div>
) : (
  <div className="new-game-cards">
    {gamesData.length === 0 ? (
      <div className="no-games-container">
        {/* <div className="no-games-icon">🎮</div> */}
        <span className="no-games-text">{t("message.no_games_found")}</span>
      </div>
    ) : (
      <>
        {gamesData.map((game, index) => (
          <GameCard
            title={game.gameTitle}
            image={game.thumbnail}
            recent={game.recent}
            recommended={game.recommended}
            isAvailable={game.is_available}
            key={index}
            index={index}
            onClick={() => handleGameCardClick(game)}
          />
        ))}

        {isLoadMore && (
          <div ref={lastElementRef} className="load-more-container">
            {isLoadingMore && (
              <div className="load-more-loading">
                <div className="loading-spinner"></div>
                <span className="loading-text">{t("common.loading")}</span>
              </div>
            )}
          </div>
        )}
      </>
    )}
  </div>
)}
        {/* <div className="newgames-cards-mobile">
                  <img src="/img/mobile-images/image 5.png" alt="gamecards"/>
                  <img src="/img/mobile-images/image 6.png" alt="gamecards"/>
              </div> */}
              </div>
      </div>
      <GameDialog
        isOpen={showGameDialog}
        onClose={() => setShowGameDialog(false)}
        gameData={selectedGame}
      />
    </>
  );
}
